import { useMemo, useState } from 'react';
import { InboxIcon, SlidersHorizontalIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';
import { useWorkspaceList } from '@/features/workspaces';
import { cn } from '@/lib/utils';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import { useArrivalStore } from '../arrivals';
import { PAGE_COPY } from '../copy';
import { groupByDay } from '../dayGroups';
import { EMPTY_VIEW } from '../emptyViews';
import type { InboxRow as Row } from '../groupByTask';
import {
  useInboxChange,
  useArrivedCount,
  useMatchingCount,
  useNotificationsPage,
} from '../hooks/useNotificationsPage';
import { useMarkRead } from '../hooks/useMarkRead';
import { useOpenNotification } from '../hooks/useOpenNotification';
import {
  activeFilterCount,
  clearedFilters,
  PAGE_STATUSES,
  RANGE_LABEL,
  STATE_LABEL,
  STATUS_LABEL,
  toInboxQuery,
  type NotificationsSearch,
} from '../pageFilter';
import { typeGroupLabel } from '../typeGroups';
import { FilterActionDialog } from './FilterActionDialog';
import { DayHeading } from './InboxPanel';
import { InboxRow } from './InboxRow';
import { PageFilters, type WorkspaceOption } from './PageFilters';
import { SelectionBar } from './SelectionBar';

const PANEL_ID = 'notifications-list';

/** One notification as a row of the bell's design: a group of one. */
function rowOf(notification: Row['latest']): Row {
  return {
    key: notification.id,
    latest: notification,
    items: [notification],
    unread: notification.read_at === null ? 1 : 0,
  };
}

/** The active filters in words, for the empty state that they emptied. */
function describeFilters(
  search: NotificationsSearch,
  workspaceName: string | undefined,
): string {
  const parts: string[] = [];
  if (search.status && search.status !== 'all') {
    parts.push(STATUS_LABEL[search.status]);
  }
  if (search.workspace) parts.push(workspaceName ?? PAGE_COPY.workspace);
  if (search.type?.length)
    parts.push(search.type.map(typeGroupLabel).join(', '));
  if (search.range === 'custom') {
    parts.push(
      [
        search.from ? `${PAGE_COPY.from} ${search.from}` : null,
        search.to ? `${PAGE_COPY.to} ${search.to}` : null,
      ]
        .filter(Boolean)
        .join(' ') || RANGE_LABEL.custom,
    );
  } else if (search.range && search.range !== 'all') {
    parts.push(RANGE_LABEL[search.range]);
  }
  if (search.state) parts.push(STATE_LABEL[search.state]);
  return parts.join(' · ');
}

/**
 * Every notification, a page at a time (spec 147): the status tabs and the
 * filters, all in the address; one row per notification under day headings;
 * Show older, and the retention line once there is nothing older.
 */
export function NotificationsPage({
  search,
  onSearchChange,
}: {
  search: NotificationsSearch;
  onSearchChange: (next: NotificationsSearch) => void;
}) {
  const status = search.status ?? 'all';
  const phone = usePhoneWidth();
  const query = useMemo(() => toInboxQuery(search), [search]);
  const page = useNotificationsPage(query);
  const mark = useMarkRead();
  const change = useInboxChange();
  const openNotification = useOpenNotification();
  const workspaceList = useWorkspaceList({ limit: 200 });
  const workspaces: WorkspaceOption[] = useMemo(
    () =>
      (workspaceList.data?.items ?? [])
        .map((workspace) => ({ id: workspace.id, name: workspace.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [workspaceList.data],
  );
  const rows = useMemo(
    () => (page.notifications ?? []).map(rowOf),
    [page.notifications],
  );
  const days = useMemo(
    () => groupByDay(rows, (row) => row.latest.created_at),
    [rows],
  );
  const filterCount = activeFilterCount(search);
  const archived = status === 'archived';
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The selection belongs to one filter: a new one starts with none.
  const queryKey = JSON.stringify(query);
  const [selection, setSelection] = useState<{
    key: string;
    ids: ReadonlySet<string>;
    allMatching: boolean;
  }>({ key: queryKey, ids: new Set(), allMatching: false });
  if (selection.key !== queryKey) {
    setSelection({ key: queryKey, ids: new Set(), allMatching: false });
  }
  // Only loaded rows can be acted on (spec 147 edge cases): one archived
  // out of this view is no longer selected.
  const selectedIds = rows
    .map((row) => row.key)
    .filter((id) => selection.ids.has(id));
  const allOnPage = rows.length > 0 && selectedIds.length === rows.length;
  const matching = useMatchingCount(query, allOnPage && page.hasMore);
  const [confirming, setConfirming] = useState<'read' | 'archive' | null>(null);

  // New arrivals since the list last loaded from the top (FR-007): counted
  // into a bar, not inserted, so nothing moves under the reader. One already
  // in the list, or of a type not picked, is not news here; nothing new is
  // archived.
  const arrivals = useArrivalStore((state) => state.arrivals);
  const lastSeq = useArrivalStore((state) => state.seq);
  const [seen, setSeen] = useState({ key: queryKey, seq: lastSeq });
  if (seen.key !== queryKey) setSeen({ key: queryKey, seq: lastSeq });
  const loadedIds = new Set(rows.map((row) => row.key));
  const candidates = archived
    ? 0
    : arrivals.filter(
        (arrival) =>
          arrival.seq > seen.seq &&
          !loadedIds.has(arrival.id) &&
          (!query.kind ||
            (arrival.kind !== null && query.kind.includes(arrival.kind))),
      ).length;
  // An arrival names only its kind. With a workspace, Needs you, a state or a
  // date set, the service counts what arrived by the same filter, so the bar
  // never promises more than Show will bring (review of !250).
  const narrowed = Boolean(
    query.workspace_id || query.group || query.state || query.created_to,
  );
  const newest = rows.reduce<string | undefined>(
    (latest, row) =>
      latest === undefined || row.latest.created_at > latest
        ? row.latest.created_at
        : latest,
    undefined,
  );
  const since = new Date(
    newest ? new Date(newest).getTime() + 1 : page.loadedAt,
  ).toISOString();
  const arrived = useArrivedCount(
    query,
    since,
    lastSeq,
    narrowed && candidates > 0,
  );
  const newCount = narrowed ? Math.min(candidates, arrived ?? 0) : candidates;

  function select(ids: Iterable<string>, allMatching = false) {
    setSelection({ key: queryKey, ids: new Set(ids), allMatching });
  }

  function toggle(id: string) {
    const next = new Set(selection.ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    select(next);
  }

  function renderRow(row: Row) {
    return (
      <InboxRow
        key={row.key}
        row={row}
        onOpen={(r) => {
          openNotification(r.latest);
        }}
        onArchive={
          archived
            ? undefined
            : (r) => {
                change.mutate({
                  kind: 'bulk',
                  ids: [r.latest.id],
                  action: 'archive',
                });
              }
        }
        onUnarchive={
          archived
            ? (r) => {
                change.mutate({
                  kind: 'bulk',
                  ids: [r.latest.id],
                  action: 'unarchive',
                });
              }
            : undefined
        }
        onMarkUnread={(r) => {
          mark.mutate({ kind: 'unread', notificationId: r.latest.id });
        }}
        onAcknowledge={(r) => {
          mark.mutate({ kind: 'acknowledge', notificationId: r.latest.id });
        }}
        selection={{
          selected: selection.ids.has(row.key),
          onToggle: () => {
            toggle(row.key);
          },
        }}
      />
    );
  }

  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[48rem] flex-col gap-4 p-4 md:p-6',
        // Room under the list for the selection bar a phone pins there.
        selectedIds.length > 0 ? 'max-md:pb-24' : '',
      )}
    >
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-xl font-semibold">
          {PAGE_COPY.title}
        </h1>
      </header>

      <div className="border-border flex flex-col gap-3 border-b">
        {/* Four short tabs fit a phone's width, so they share it rather than
            bunch at the left (spec 274). */}
        <TabsList aria-label="Status" className="max-md:gap-0">
          {PAGE_STATUSES.map((value) => (
            <TabsTrigger
              key={value}
              className="max-md:flex-1 max-md:justify-center"
              selected={status === value}
              panelId={PANEL_ID}
              onClick={() => {
                onSearchChange({
                  ...search,
                  status: value === 'all' ? undefined : value,
                });
              }}
            >
              {STATUS_LABEL[value]}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {/* The status tabs stay on a phone; the rest fold into one pill that
          opens them in a sheet (spec 147 Q12). There the pill shares its row
          with "Select all on this page", each half the width, rather than
          each standing alone on a row of its own (spec 274). */}
      <div
        role="group"
        aria-label={PAGE_COPY.filters}
        className="flex flex-wrap items-center gap-2 max-md:flex-nowrap max-md:[&>*]:flex-1 max-md:[&>*]:justify-center"
      >
        <div className="hidden md:contents">
          <PageFilters
            search={search}
            workspaces={workspaces}
            onChange={onSearchChange}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setFiltersOpen(true);
          }}
          className={`${HEADER_PILL} ${filterCount > 0 ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE} md:hidden`}
        >
          <SlidersHorizontalIcon aria-hidden="true" className="size-3" />
          {filterCount > 0
            ? `${PAGE_COPY.filters} · ${String(filterCount)}`
            : PAGE_COPY.filters}
        </button>
        {phone && rows.length > 0 && selectedIds.length === 0 ? (
          <button
            type="button"
            onClick={() => {
              select(rows.map((row) => row.key));
            }}
            className={`${HEADER_PILL} ${HEADER_PILL_IDLE}`}
          >
            {PAGE_COPY.selectPage}
          </button>
        ) : null}
      </div>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>{PAGE_COPY.filters}</SheetTitle>
          </SheetHeader>
          {/* One full-width row per filter, its chevron at the far end
              (spec 274): the pills at their own width read as a ragged
              column of buttons. */}
          <SheetBody className="flex flex-col items-stretch gap-3 pb-6 [&>[aria-haspopup]]:w-full [&>[aria-haspopup]]:max-w-none [&>[aria-haspopup]]:rounded-lg [&>[aria-haspopup]>:last-child]:ms-auto">
            <PageFilters
              search={search}
              workspaces={workspaces}
              onChange={onSearchChange}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <div id={PANEL_ID} role="tabpanel" className="flex flex-col">
        <div
          aria-live="polite"
          className="sticky top-2 z-10 flex justify-center"
        >
          {newCount > 0 ? (
            <button
              type="button"
              onClick={() => {
                setSeen({ key: queryKey, seq: lastSeq });
                void page.refetch();
              }}
              className="bg-primary text-primary-foreground shadow-raised focus-visible:ring-ring mb-2 rounded-full px-3 py-1 text-xs font-medium tabular-nums focus-visible:ring-3 focus-visible:outline-none"
            >
              {PAGE_COPY.arrivals(newCount)}
            </button>
          ) : null}
        </div>
        {page.isPending ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            Loading…
          </p>
        ) : page.loadFailed ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-muted-foreground text-sm">
              Notifications could not be loaded.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void page.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <Empty
            search={search}
            filterCount={filterCount}
            workspaceName={
              workspaces.find((w) => w.id === search.workspace)?.name
            }
            onClear={() => {
              onSearchChange(clearedFilters(search));
            }}
          />
        ) : (
          <>
            {/* On a phone "Select all on this page" sits beside Filters. */}
            {selectedIds.length > 0 || !phone ? (
              <div className="pb-2">
                {selectedIds.length > 0 ? (
                  <SelectionBar
                    count={selectedIds.length}
                    archived={archived}
                    allOnPage={allOnPage}
                    matching={page.hasMore ? matching.total : rows.length}
                    allMatching={selection.allMatching && allOnPage}
                    busy={change.isPending}
                    onAction={(action) => {
                      change.mutate(
                        { kind: 'bulk', ids: selectedIds, action },
                        {
                          onSuccess: () => {
                            select([]);
                          },
                        },
                      );
                    }}
                    onSelectPage={() => {
                      select(rows.map((row) => row.key));
                    }}
                    onSelectMatching={() => {
                      select(selection.ids, true);
                    }}
                    onFilterAction={setConfirming}
                    onClear={() => {
                      select([]);
                    }}
                  />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      select(rows.map((row) => row.key));
                    }}
                  >
                    {PAGE_COPY.selectPage}
                  </Button>
                )}
              </div>
            ) : null}
            <ul aria-label="Notifications" className="flex flex-col">
              {days.map((day) => [
                <DayHeading key={`day-${day.key}`}>{day.label}</DayHeading>,
                ...day.rows.map(renderRow),
              ])}
            </ul>
          </>
        )}
        {page.hasMore ? (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 self-center"
            disabled={page.loadingMore}
            onClick={() => void page.loadMore()}
          >
            {page.loadingMore ? 'Loading…' : PAGE_COPY.showOlder}
          </Button>
        ) : !page.isPending && !page.loadFailed ? (
          <p className="text-muted-foreground pt-4 pb-2 text-center text-xs">
            {PAGE_COPY.retention}
          </p>
        ) : null}
      </div>

      <FilterActionDialog
        action={confirming}
        query={query}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        onConfirm={(action) => {
          change.mutate(
            {
              kind: action === 'archive' ? 'archive-all' : 'read-all',
              filter: query,
            },
            {
              onSuccess: () => {
                select([]);
              },
            },
          );
        }}
      />
    </div>
  );
}

/**
 * Says which filter emptied the list (spec 147 US1-5). A status tab alone
 * says what the bell says; Needs you with Resolved explains itself; anything
 * else names the filters and offers to clear them.
 */
function Empty({
  search,
  filterCount,
  workspaceName,
  onClear,
}: {
  search: NotificationsSearch;
  filterCount: number;
  workspaceName: string | undefined;
  onClear: () => void;
}) {
  const status = search.status ?? 'all';
  if (filterCount === 0) {
    const view = EMPTY_VIEW[status];
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <view.icon
          aria-hidden="true"
          className="text-muted-foreground size-6"
          strokeWidth={1.5}
        />
        <p className="text-muted-foreground text-sm">{view.line}</p>
      </div>
    );
  }
  const needsYouResolved =
    status === 'needs_you' && search.state === 'resolved';
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <InboxIcon
        aria-hidden="true"
        className="text-muted-foreground size-6"
        strokeWidth={1.5}
      />
      <p className="text-foreground text-sm font-medium">
        {PAGE_COPY.emptyFiltered}
      </p>
      <p className="text-muted-foreground text-xs">
        {needsYouResolved
          ? PAGE_COPY.needsYouResolved
          : describeFilters(search, workspaceName)}
      </p>
      <Button variant="outline" size="sm" className="mt-1" onClick={onClear}>
        {PAGE_COPY.clearFilters}
      </Button>
    </div>
  );
}
